import { Dispatch, SetStateAction, createContext, useContext, useState } from 'react';
import { Dimensions } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SharedValue, useSharedValue, withSpring } from 'react-native-reanimated';
import { Platform } from 'utils/platform';

type BottomSheetContextProps = {
  children?: React.ReactNode;
  visible: boolean;
  setVisible: (data: boolean) => void;
  handleToggleSheet: (isFullView?: boolean) => void;
  currentUrl: string;
  setCurrentUrl: (data: string) => void;
  liveMenuItems: LiveMenuItems[];
  setLiveMenuItems: Dispatch<SetStateAction<LiveMenuItems[]>>;
  menuOptions: any;
  setMenuOptions: (data: string) => void | undefined;
  currentMenu: CurrentMenu;
  setCurrentMenu: (data: CurrentMenu) => void;
  translateY: SharedValue<number>;
  scrollTo: (data: number) => void;
  context: SharedValue<{
    y: number;
  }>;
};

type CurrentMenu = { value: number; name: string; image?: string };

const BottomSheetContext = createContext<BottomSheetContextProps>({} as BottomSheetContextProps);

const getMidTranslateYDivisor = () => {
  if (Platform.isMobileWeb('android')) {
    return 3.1;
  }

  return 3.8;
};

const getNavigationBarHeight = (isFullView: boolean = false) => {
  const { height: screenHeight } = Dimensions.get('screen');
  const { height: windowHeight } = Dimensions.get('window');
  const screenDifference = screenHeight - windowHeight;
  const handleHeight = isFullView ? screenDifference <= 48 : screenDifference < 48;

  if (Platform.OS === 'android') return handleHeight ? 0 : (screenHeight - windowHeight) / 2;

  return 0;
};

export const HEADER_HEIGHT = Platform.OS !== 'web' ? 45 : 88;
export const FOOTER_HEIGHT = Platform.OS !== 'web' ? 75 : 0;
export const SCREEN_HEIGHT = Dimensions.get('window').height + getNavigationBarHeight(true);
export const MAX_TRANSLATE_Y = SCREEN_HEIGHT / 1.5 - HEADER_HEIGHT;
export const MID_TRANSLATE_Y =
  SCREEN_HEIGHT / getMidTranslateYDivisor() + HEADER_HEIGHT + 40 - getNavigationBarHeight();
export const MID_TRANSLATE_Y_WEB = SCREEN_HEIGHT / 3.2;

export const MIN_TRANSLATE_Y = SCREEN_HEIGHT / 5;

export const BottomSheetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const [menuOptions, setMenuOptions] = useState('');
  const [liveMenuItems, setLiveMenuItems] = useState<LiveMenuItems[]>([]);
  const [currentMenu, setCurrentMenu] = useState<CurrentMenu>({
    value: 0,
    name: '',
    image: '',
  });
  const [currentUrl, setCurrentUrl] = useState('');
  const translateY = useSharedValue(SCREEN_HEIGHT);
  const context = useSharedValue({ y: SCREEN_HEIGHT });

  const handleToggleSheet = (isFullView = true) => {
    if (visible) {
      scrollTo(SCREEN_HEIGHT);
    } else {
      scrollTo(
        !isFullView
          ? Platform.OS === 'web'
            ? -MID_TRANSLATE_Y_WEB
            : -MID_TRANSLATE_Y
          : -MAX_TRANSLATE_Y
      );
    }

    setVisible(!visible);
  };

  const scrollTo = (destination: any) => {
    translateY.value = withSpring(destination, { damping: 50 });
  };

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <BottomSheetContext.Provider
        value={{
          visible,
          setVisible,
          handleToggleSheet,
          menuOptions,
          setMenuOptions,
          liveMenuItems,
          setLiveMenuItems,
          currentMenu,
          setCurrentMenu,
          currentUrl,
          setCurrentUrl,
          translateY,
          scrollTo,
          context,
        }}>
        {children}
      </BottomSheetContext.Provider>
    </GestureHandlerRootView>
  );
};

export const useBottomSheet = () => useContext(BottomSheetContext);

export default BottomSheetContext;
